import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ToastService, ToastVariant } from '../../services/toast/toast.service';

/**
 * ToastContainerComponent — fixed bottom-right toast stack.
 *
 * Renders the live `ToastService.toasts()` signal, matching the `ToastContainer`
 * in `ui_kits/customer/portal/Shell.jsx`: `#071929` surface, amber left border,
 * blue-tint shadow, column-reverse stack. Drop one instance at the app root
 * (`<bodyanalytics-toast-container />`); every toast then routes through the
 * injected `ToastService`. Auto-dismissal is owned by the service.
 */
@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-toast-container',
  templateUrl: './toast-container.component.html',
  styleUrls: ['./toast-container.component.scss'],
})
export class ToastContainerComponent {
  private readonly toastService = inject(ToastService);

  /** Live toast stack (oldest first; CSS column-reverse paints newest on top). */
  protected readonly toasts = this.toastService.toasts;

  /** Default Boxicon per variant when a caller does not supply one. */
  private readonly variantIcons: Record<ToastVariant, string> = {
    success: 'bx-check-circle',
    error: 'bx-error-circle',
    info: 'bx-info-circle',
  };

  protected iconFor(variant: ToastVariant, icon?: string): string {
    return icon ?? this.variantIcons[variant];
  }

  protected dismiss(id: number): void {
    this.toastService.dismiss(id);
  }
}
